import { Selector } from 'components/Form/Selectors/Selector';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { updateCustomer } from 'store/persisted.slice';
import { updateCustomer as updateAuthCustomer } from 'store/auth.slice';
import {
  setCustomerShiftConfigData,
  setCustomerShiftConfigLoading,
} from 'store/customer-shift-config.slice';
import { Backdrop, CircularProgress } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useResetReduxAndWebStorage } from 'hooks/useResetReduxAndWebStorage';
import InternalUserHandler from 'handlers/internal.user.handler';
import AuthHandler from 'handlers/auth.handler';
import NavigationMapHandler from 'handlers/navigation-map.handler';
import CustomerShiftConfigAPI from 'api/customer-shift-config.api';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import useAppTheme from 'hooks/useAppTheme';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { useTranslation } from 'react-i18next';

/**
 * Customer select component used in the top bar to allow
 * users to change the current customer.
 *
 * @returns  JSX.Element
 */
export const CustomerSelector = ({
  selectorOptions,
}: {
  selectorOptions: Array<{
    id: string;
    display: string;
    code: string;
    unitSystem: string;
    timezone: string;
    mineType: string;
    proj4String: string | null;
    localOriginEasting: number | null;
    localOriginNorthing: number | null;
    modules?: {
      isPartsModule: boolean;
      isTiresModule: boolean;
      isMachinesModule: boolean;
    };
  }>;
}): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { enableDarkTheme } = useAppTheme();
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const resetApp = useResetReduxAndWebStorage();
  const internalUserHandler = new InternalUserHandler();
  const navigationMapHandler = new NavigationMapHandler();

  const authHandler = new AuthHandler();

  const persistedReducer = useAppSelector((state) => state.persistedReducer);
  const currentAuthCustomer = useAppSelector(
    (state) => state.authReducer.customer
  );
  const { latestSensorPollingId, notificationPollingId } = useAppSelector(
    (state) => state.appReducer
  );

  const persistedCustomerId = persistedReducer?.customer?.id;

  const isPersistedCustomerValid = useMemo(
    () => selectorOptions.some((option) => option.id === persistedCustomerId),
    [selectorOptions, persistedCustomerId]
  );

  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(
    isPersistedCustomerValid ? persistedCustomerId : ''
  );

  const [isLaunching, setIsLaunching] = useState(false);
  const didNavigateRef = useRef(false);

  const handleLaunchDashboard = async () => {
    if (isLaunching) return;
    try {
      setIsLaunching(true);
      didNavigateRef.current = false;

      const selectedCustomerOption = selectorOptions.find(
        (option) => option.id === selectedCustomerId
      );
      if (!selectedCustomerOption) return;
      const selectedCustomerCode = selectedCustomerOption.code;
      const selectedCustomerModules = selectedCustomerOption.modules ?? {
        isPartsModule: false,
        isTiresModule: false,
        isMachinesModule: false,
      };

      const fetchAndStoreCustomerShiftConfig = async (
        explicitToken?: string,
        explicitCustomerId?: string
      ) => {
        dispatch(setCustomerShiftConfigLoading(true));
        try {
          const shiftConfig =
            await CustomerShiftConfigAPI.getCustomerShiftConfig(
              selectedCustomerCode,
              explicitToken,
              explicitCustomerId
            );
          dispatch(
            setCustomerShiftConfigData({
              custCode: selectedCustomerCode,
              data: shiftConfig,
            })
          );
        } catch (error) {
          console.error('Error while fetching customer shift config', error);
          dispatch(setCustomerShiftConfigLoading(false));
        }
      };

      const hydratedToken = await authHandler.hydrateToken();

      if (selectedCustomerId === persistedCustomerId) {
        void fetchAndStoreCustomerShiftConfig(
          hydratedToken,
          selectedCustomerId
        );
        await navigationMapHandler.getNavigationMap(true, selectedCustomerCode);
        didNavigateRef.current = true;
        navigate('/modules');
        return;
      }

      if (latestSensorPollingId) {
        // stopping the getDeviceLatestSensorDataAll call for the prev customer
        clearTimeout(latestSensorPollingId);
      }
      if (notificationPollingId) {
        // stopping the getAllLatest notifcation call for the prev customer
        clearTimeout(notificationPollingId);
      }

      await resetApp();

      // since all the redux is flushed out we have to rehydrate the customer and user info
      await internalUserHandler.userInfoSync();
      const freshTokenAfterReset = await authHandler.hydrateToken();
      await authHandler.hydrateClaims();

      const tempCustomer = {
        id: selectedCustomerId,
        name: selectedCustomerOption.display,
        code: selectedCustomerOption.code,
        unitSystem: selectedCustomerOption.unitSystem,
        timezone: selectedCustomerOption.timezone,
        proj4String: selectedCustomerOption.proj4String ?? null,
        localOriginEasting: selectedCustomerOption.localOriginEasting ?? null,
        localOriginNorthing: selectedCustomerOption.localOriginNorthing ?? null,
      };
      dispatch(updateCustomer(tempCustomer));

      // Also update authReducer with modules so it's available immediately
      dispatch(
        updateAuthCustomer({
          ...currentAuthCustomer,
          id: selectedCustomerId,
          name: selectedCustomerOption.display,
          code: selectedCustomerOption.code,
          unitSystem: selectedCustomerOption.unitSystem,
          timezone: selectedCustomerOption.timezone,
          mineType: selectedCustomerOption.mineType ?? '',
          proj4String: selectedCustomerOption.proj4String ?? null,
          modules: selectedCustomerModules,
        })
      );

      void fetchAndStoreCustomerShiftConfig(
        freshTokenAfterReset ?? hydratedToken,
        selectedCustomerId
      );
      await navigationMapHandler.getNavigationMap(true, selectedCustomerCode);
      didNavigateRef.current = true;
      navigate('/modules');
    } catch (error) {
      console.error('Error while selecting customer', error);
    } finally {
      setIsLaunching(false);
    }
  };

  useEffect(() => {
    if (isPersistedCustomerValid) {
      setSelectedCustomerId(persistedCustomerId);
    } else {
      setSelectedCustomerId('');
    }
  }, [persistedCustomerId, isPersistedCustomerValid]);

  return (
    <>
      <Backdrop
        open={isLaunching}
        sx={{
          color: '#fff',
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Selector
        inputProps={{
          sx: {
            fontSize: '18px',
            fontWeight: 'medium',
            borderRadius: 2,
          },
        }}
        selectSx={{
          borderRadius: 2,
          backgroundColor: '#FFFFFF',
          color: '#1A1A1A',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D9DADA',
          },
        }}
        fontSize="16px"
        size="medium"
        value={selectedCustomerId}
        onChange={(event) => {
          const tempVal = event.target.value.toString();
          setSelectedCustomerId(tempVal);
        }}
        selectorOptions={selectorOptions}
        disabled={isLaunching}
      ></Selector>

      <CustomLoadingButton
        variant="contained"
        size="large"
        endIcon={<ArrowForward />}
        disabled={!selectedCustomerId || isLaunching}
        sx={{
          backgroundColor: themeColors.primary,
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          py: 1.5,
          borderRadius: 2,
          boxShadow: `0 4px 6px -1px ${themeColors.primary}33`,
          '&:hover': {
            backgroundColor: themeColors.navbarActiveBg,
            boxShadow: `0 10px 15px -3px ${themeColors.primary}4d`,
          },
        }}
        onClick={handleLaunchDashboard}
        loading={isLaunching}
        loadingText={t('cdp_loading')}
      >
        {t('launch_dashboard')}
      </CustomLoadingButton>
    </>
  );
};
